import type {
  PocketType,
  CashflowYearlyReport,
  YearlyReportMonth,
} from '@/types/cashflow';

export const MONTH_NAMES_ID = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

export const SHORT_MONTH_NAMES_ID = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mei',
  'Jun',
  'Jul',
  'Agu',
  'Sep',
  'Okt',
  'Nov',
  'Des',
];

function toValidNumber(val: unknown): number {
  if (val === null || val === undefined) return 0;
  const num = typeof val === 'number' ? val : Number(val);
  return isNaN(num) ? 0 : num;
}

export function formatRupiah(amount: unknown): string {
  const safe = toValidNumber(amount);
  const isNegative = safe < 0;
  const abs = Math.abs(safe);
  const formatted = new Intl.NumberFormat('id-ID').format(abs);
  return `${isNegative ? '-Rp ' : 'Rp '}${formatted}`;
}

export function formatRupiahPlain(amount: unknown): string {
  const safe = toValidNumber(amount);
  const abs = Math.abs(safe);
  const formatted = new Intl.NumberFormat('id-ID').format(abs);
  return `Rp ${formatted}`;
}

export function formatRupiahSigned(amount: unknown, forcePositiveSign = true): string {
  const safe = toValidNumber(amount);
  const isNegative = safe < 0;
  const abs = Math.abs(safe);
  const formatted = new Intl.NumberFormat('id-ID').format(abs);
  if (isNegative) {
    return `- Rp ${formatted}`;
  }
  if (forcePositiveSign) {
    return `+ Rp ${formatted}`;
  }
  return `Rp ${formatted}`;
}

export function formatJuta(amount: unknown, signed = false): string {
  const safe = toValidNumber(amount);
  const isNegative = safe < 0;
  const absInJuta = Math.abs(safe) / 1_000_000;
  const formatted = absInJuta.toFixed(1).replace('.', ',');
  if (signed) {
    if (isNegative) return `-${formatted}jt`;
    return `+${formatted}jt`;
  }
  return `${formatted}jt`;
}

export function formatDateIndo(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = String(date.getDate()).padStart(2, '0');
  const month = SHORT_MONTH_NAMES_ID[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export function formatShortDateTime(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const month = SHORT_MONTH_NAMES_ID[d.getMonth()] || '';
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month}, ${hours}.${mins}`;
}

export interface PocketTypeMeta {
  type: PocketType;
  label: string;
  icon: string;
  badgeBg: string;
  badgeText: string;
}

export const POCKET_TYPES: Record<PocketType, PocketTypeMeta> = {
  rekening: {
    type: 'rekening',
    label: 'Rekening Bank',
    icon: 'ph:bank-bold',
    badgeBg: 'bg-blue-50',
    badgeText: 'text-blue-600',
  },
  kas_tunai: {
    type: 'kas_tunai',
    label: 'Kas Tunai',
    icon: 'ph:money-bold',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-600',
  },
  ewallet: {
    type: 'ewallet',
    label: 'E-Wallet',
    icon: 'ph:device-mobile-bold',
    badgeBg: 'bg-purple-50',
    badgeText: 'text-purple-600',
  },
  sporto_wallet: {
    type: 'sporto_wallet',
    label: 'Dompet Sporto',
    icon: 'ph:wallet-bold',
    badgeBg: 'bg-orange-50',
    badgeText: 'text-[#FC480D]',
  },
  lainnya: {
    type: 'lainnya',
    label: 'Lainnya',
    icon: 'ph:dots-three-circle-bold',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-600',
  },
};

export async function exportYearlyReportToExcel(
  report: CashflowYearlyReport | null,
  year: number,
  communityName?: string
): Promise<void> {
  const XLSX = await import('xlsx');
  const { downloadOrShareBlob } = await import('@/utils/downloadBlob');

  const rows: YearlyReportMonth[] = report?.rows ?? report?.months ?? [];
  const commTitle = communityName || 'Sporto Community';

  const aoa: (string | number)[][] = [
    ['REKAPITULASI ARUS KAS TAHUNAN'],
    ['Komunitas', commTitle],
    ['Tahun Anggaran', year],
    ['Tanggal Cetak', new Date().toLocaleDateString('id-ID', { dateStyle: 'long' })],
    [],
    [
      'No',
      'Bulan',
      'Jumlah Event',
      'Pemasukan (PAID)',
      'Menunggu Pembayaran (Pending)',
      'Pengeluaran',
      'Saldo Bersih (NET)',
    ],
  ];

  let calculatedEvents = 0;
  let calculatedIncome = 0;
  let calculatedPending = 0;
  let calculatedExpense = 0;
  let calculatedNet = 0;

  for (let m = 1; m <= 12; m++) {
    const row = rows.find((r) => r.month === m);
    const monthName = MONTH_NAMES_ID[m - 1];
    const eventCount = row?.event_count ?? 0;
    const income = row?.income ?? row?.total_income ?? 0;
    const pending = row?.pending ?? 0;
    const expense = row?.expense ?? row?.total_expense ?? 0;
    const net = row?.net ?? row?.net_balance ?? (income - expense);

    calculatedEvents += eventCount;
    calculatedIncome += income;
    calculatedPending += pending;
    calculatedExpense += expense;
    calculatedNet += net;

    aoa.push([
      m,
      monthName,
      eventCount,
      income,
      pending,
      expense,
      net,
    ]);
  }

  const grandTotalEvents = report?.total_events ?? calculatedEvents;
  const grandTotalIncome = report?.total_income ?? calculatedIncome;
  const grandTotalPending = report?.total_pending ?? calculatedPending;
  const grandTotalExpense = report?.total_expense ?? calculatedExpense;
  const grandTotalNet = report?.total_net ?? report?.net_balance ?? calculatedNet;

  aoa.push([
    '',
    'TOTAL TAHUNAN',
    grandTotalEvents,
    grandTotalIncome,
    grandTotalPending,
    grandTotalExpense,
    grandTotalNet,
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Set column widths
  ws['!cols'] = [
    { wch: 6 },
    { wch: 18 },
    { wch: 15 },
    { wch: 22 },
    { wch: 28 },
    { wch: 22 },
    { wch: 22 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Kas_${year}`);

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const safeCommunity = commTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
  const filename = `Rekap_Kas_${year}_${safeCommunity}.xlsx`;

  await downloadOrShareBlob(blob, filename, { title: `Rekap Kas ${year}` });
}

export async function exportYearlyReportToCSV(
  report: CashflowYearlyReport | null,
  year: number,
  communityName?: string
): Promise<void> {
  const { downloadOrShareBlob } = await import('@/utils/downloadBlob');

  const rows: YearlyReportMonth[] = report?.rows ?? report?.months ?? [];
  const commTitle = communityName || 'Sporto Community';

  const escapeCsv = (str: string | number) => {
    const val = String(str ?? '');
    if (val.includes(',') || val.includes('"') || val.includes('\n')) {
      return `"${val.replace(/"/g, '""')}"`;
    }
    return val;
  };

  const csvLines: string[] = [];

  csvLines.push(['REKAPITULASI ARUS KAS TAHUNAN'].map(escapeCsv).join(','));
  csvLines.push(['Komunitas', commTitle].map(escapeCsv).join(','));
  csvLines.push(['Tahun Anggaran', String(year)].map(escapeCsv).join(','));
  csvLines.push(['Tanggal Cetak', new Date().toLocaleDateString('id-ID', { dateStyle: 'long' })].map(escapeCsv).join(','));
  csvLines.push('');

  csvLines.push(
    [
      'No',
      'Bulan',
      'Jumlah Event',
      'Pemasukan (PAID)',
      'Menunggu Pembayaran (Pending)',
      'Pengeluaran',
      'Saldo Bersih (NET)',
    ]
      .map(escapeCsv)
      .join(',')
  );

  let calculatedEvents = 0;
  let calculatedIncome = 0;
  let calculatedPending = 0;
  let calculatedExpense = 0;
  let calculatedNet = 0;

  for (let m = 1; m <= 12; m++) {
    const row = rows.find((r) => r.month === m);
    const monthName = MONTH_NAMES_ID[m - 1];
    const eventCount = row?.event_count ?? 0;
    const income = row?.income ?? row?.total_income ?? 0;
    const pending = row?.pending ?? 0;
    const expense = row?.expense ?? row?.total_expense ?? 0;
    const net = row?.net ?? row?.net_balance ?? (income - expense);

    calculatedEvents += eventCount;
    calculatedIncome += income;
    calculatedPending += pending;
    calculatedExpense += expense;
    calculatedNet += net;

    csvLines.push(
      [
        m,
        monthName,
        eventCount,
        income,
        pending,
        expense,
        net,
      ]
        .map(escapeCsv)
        .join(',')
    );
  }

  const grandTotalEvents = report?.total_events ?? calculatedEvents;
  const grandTotalIncome = report?.total_income ?? calculatedIncome;
  const grandTotalPending = report?.total_pending ?? calculatedPending;
  const grandTotalExpense = report?.total_expense ?? calculatedExpense;
  const grandTotalNet = report?.total_net ?? report?.net_balance ?? calculatedNet;

  csvLines.push(
    [
      '',
      'TOTAL TAHUNAN',
      grandTotalEvents,
      grandTotalIncome,
      grandTotalPending,
      grandTotalExpense,
      grandTotalNet,
    ]
      .map(escapeCsv)
      .join(',')
  );

  // Prepend UTF-8 BOM so Microsoft Excel automatically recognizes Unicode encoding
  const csvContent = '\uFEFF' + csvLines.join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  const safeCommunity = commTitle.replace(/[^a-zA-Z0-9_-]/g, '_');
  const filename = `Rekap_Kas_${year}_${safeCommunity}.csv`;

  await downloadOrShareBlob(blob, filename, { title: `Rekap Kas ${year}` });
}

